import { useAtomCallback } from 'jotai/utils';
import { cloneDeep } from 'lodash-es';
import { useCallback } from 'react';
import { useToastMessageState } from '../../../../components/ToastMessage/ToastMessageProvider';
import { ICompanyDataModel } from '../../../../data-models/company.data-model';
import {
  activeCompanyListIdStateJ,
  companyListStateJ,
  listsStateJ,
  trackingDataStateJ,
} from '../../../../services/state/ListsStateJ';
import { IListItemDataModel } from '../../../../data-models/list-item.data-model';
import { updateCompany } from '../../../../services/queries/MaggieCompanyQueries';
import { IListDataModel } from '../../../../data-models/list.data-model';
import { LoadingId } from '../../../../types';
import { useLoadingBarState } from '../../../../components/LoadingBar/LoadingBarContext';
import { deleteListItem, updateListItem } from '../../../../services/queries/MaggieListQueries';
import { partialEqualsIgnoreUnsetValues } from '../../../../util/partialEquals';
import { ITrackCompanyPayload, TrackingAction } from '../../../../data-models/tracking.data-model';
import { trackCompany } from '../../../../services/queries/MaggieTrackingQueries';
import { getErrorMessage } from '../../../../services/queryHelpers';
import { useCurrentUser } from '../../../../services/hooks/useCurrentUser';
import { getForesightStore } from '../../../../util/jotai-store';
import { companyStateJ, getCompanySyncIfResolved } from '../../../../services/state/CompanyStateJ';

export function useCompanyActions() {
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const { actions } = useLoadingBarState();
  const currentUser = useCurrentUser();

  const _handleTrackCompany = useAtomCallback(
    useCallback(
      async (
        get,
        set,
        {
          company,
          action,
          userId,
          payload,
          lists,
        }: {
          company: ICompanyDataModel;
          action: TrackingAction;
          userId: number;
          payload?: ITrackCompanyPayload;
          lists?: IListDataModel[] | null;
        }
      ) => {
        if (action === 'untrack' && !company.followers) {
          throw new Error('Company does not have followers');
        }

        const jotaiStore = getForesightStore();
        const prevTracking = await get(trackingDataStateJ);
        const allLists = lists ?? (await get(listsStateJ));
        const followedListId = allLists?.find((list) => list.name === 'Tracked Companies')?.id;
        const prevFollowedList = followedListId ? get(companyListStateJ(followedListId)) : null;
        const currentCompany = await jotaiStore.get(companyStateJ(company.id));

        try {
          const data = await trackCompany(company.id, action, payload);

          const updatedFollowers =
            action === 'track'
              ? [...(company.followers ?? []), userId]
              : company.followers!.filter((id) => id !== userId);

          if (prevTracking != null) {
            set(
              trackingDataStateJ,
              action === 'track'
                ? [...prevTracking, data]
                : prevTracking.filter((item) => item.companyId !== company.id)
            );
          }

          if (followedListId && prevFollowedList != null) {
            const current = prevFollowedList;
            if (
              action === 'track' &&
              current.items.find((item: IListItemDataModel) => item.companyId === company.id)
            ) {
              // already in list — no-op
            } else if (action === 'track') {
              set(companyListStateJ(followedListId), {
                versions: [...current.versions],
                items: [
                  {
                    companyId: company.id,
                    listId: followedListId,
                    id: company.id,
                    updatedAt: new Date().toISOString(),
                  } as IListItemDataModel,
                  ...current.items,
                ],
              });
            } else {
              set(companyListStateJ(followedListId), {
                versions: [...current.versions],
                items: current.items.filter((item) => item.companyId !== company.id),
              });
            }
          }

          if (currentCompany) {
            jotaiStore.set(companyStateJ(company.id), {
              ...currentCompany,
              followers: updatedFollowers,
            });
          }
        } catch (err) {
          console.error(err);
          if (prevTracking != null) {
            set(trackingDataStateJ, prevTracking);
          }
          if (followedListId) {
            set(companyListStateJ(followedListId), prevFollowedList);
          }
          const message = getErrorMessage(err, 'Failed to update track status');
          pushErrorToast({ message });
        }
      },
      [pushErrorToast]
    )
  );

  /** @deprecated Use useUpdateCompanyAndState instead */
  const handleUpdateCompany = useCallback(
    async (
      {
        companyId,
        payload,
      }: {
        companyId: number;
        payload: Partial<ICompanyDataModel>;
      },
      displayToastMessage = true
    ) => {
      const jotaiStore = getForesightStore();
      const currentCompany = getCompanySyncIfResolved(companyId) ?? undefined;
      if (currentCompany && partialEqualsIgnoreUnsetValues(payload, currentCompany)) {
        return;
      }

      actions.startLoading(LoadingId.updateCompany);

      if (currentCompany) {
        jotaiStore.set(companyStateJ(companyId), { ...cloneDeep(currentCompany), ...payload });
      }

      try {
        await updateCompany({ companyId: companyId, payload });
        if (displayToastMessage) {
          pushSuccessToast({ message: 'Company updated successfully' });
        }
      } catch (err) {
        console.error(err);
        if (currentCompany) {
          jotaiStore.set(companyStateJ(companyId), currentCompany);
        }
        pushErrorToast({ message: 'Failed to update company' });
      } finally {
        actions.stopLoading(LoadingId.updateCompany);
      }
    },
    [actions, pushErrorToast, pushSuccessToast]
  );

  const handleChangeHideStatus = useAtomCallback(
    useCallback(
      async (
        get,
        set,
        {
          action,
          listItem,
          userId,
        }: {
          action: boolean;
          listItem: IListItemDataModel | null;
          userId: number;
        }
      ) => {
        if (!listItem) {
          return;
        }
        const payload: Partial<IListItemDataModel> = { action, actionType: action ? 'hidden' : null };
        const clonedListItem = cloneDeep(listItem);
        const updatedListItem: IListItemDataModel = {
          ...clonedListItem,
          ...payload,
          updatedAt: new Date().toISOString(),
        };

        const jotaiStore = getForesightStore();
        const companyBeforeHide = getCompanySyncIfResolved(listItem.companyId);
        const currentCompany = companyBeforeHide;
        const updatedCompany = !currentCompany ? null : { ...cloneDeep(currentCompany), assignee: userId };
        jotaiStore.set(companyStateJ(listItem.companyId), updatedCompany);

        const activeListId = get(activeCompanyListIdStateJ);
        const prevActiveList = activeListId ? get(companyListStateJ(activeListId)) : null;
        const prevLists = await get(listsStateJ);

        if (activeListId && prevActiveList) {
          const updatedItems = prevActiveList.items.map((item) =>
            item.id === listItem.id ? updatedListItem : item
          );
          set(companyListStateJ(activeListId), {
            ...cloneDeep(prevActiveList),
            items: updatedItems,
          });
        }

        if (prevLists) {
          set(
            listsStateJ,
            prevLists.map((list) => {
              if (list.id === listItem.listId) {
                return {
                  ...cloneDeep(list),
                  numberOfItems: payload.action ? list.numberOfItems - 1 : list.numberOfItems + 1,
                };
              }
              return list;
            })
          );
        }

        try {
          await updateListItem(payload, listItem.id);
          await updateCompany({
            payload: { assignee: userId } as Record<string, unknown>,
            companyId: listItem.companyId,
          });
        } catch (err) {
          console.error(err);
          if (activeListId) {
            set(companyListStateJ(activeListId), prevActiveList);
          }
          if (prevLists) {
            set(listsStateJ, prevLists);
          }
          jotaiStore.set(companyStateJ(listItem.companyId), companyBeforeHide ?? null);
          pushErrorToast({ message: 'Failed to update list item' });
        }
      },
      [pushErrorToast]
    )
  );

  const handleTrackCompany2 = useCallback(
    async (company: ICompanyDataModel, data: Partial<ITrackCompanyPayload>) => {
      const payload = {
        ...data,
        snoozeDate: data.snoozeDate ?? null,
        snoozeComment: data.snoozeComment ?? null,
      };

      await _handleTrackCompany({
        company,
        action: 'track',
        userId: data.userId ?? currentUser.id,
        payload,
      });
    },
    [currentUser.id, _handleTrackCompany]
  );

  const handleUntrackCompany = useCallback(
    async (company: ICompanyDataModel) => {
      await _handleTrackCompany({
        company,
        action: 'untrack',
        userId: currentUser.id,
      });
    },
    [currentUser.id, _handleTrackCompany]
  );

  return {
    handleUpdateCompany,
    handleTrackCompany2,
    handleUntrackCompany,
    handleChangeHideStatus,
  };
}

export function useDeleteCompanyItemFromList() {
  const { actions } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();

  return useAtomCallback(
    useCallback(
      async (get, set, itemId: number, listId: number) => {
        actions.startLoading(LoadingId.deleteCompanyItemFromList);
        const currentList = get(companyListStateJ(listId));
        const allLists = await get(listsStateJ);
        const updatedList = currentList?.items.filter((item) => item.id !== itemId);
        try {
          await deleteListItem(itemId);
          if (allLists) {
            set(
              listsStateJ,
              allLists.map((list) => {
                if (list.id === listId) {
                  return { ...list, numberOfItems: list.numberOfItems - 1 };
                }
                return list;
              })
            );
          }
          set(companyListStateJ(listId), {
            ...currentList,
            items: updatedList ?? [],
            versions: currentList?.versions ?? [],
          });
          pushSuccessToast({ message: 'Company removed from list' });
          return true;
        } catch (err) {
          console.error(err);
          set(companyListStateJ(listId), currentList);
          pushErrorToast({ message: 'Failed to delete company from list' });
          return false;
        } finally {
          actions.stopLoading(LoadingId.deleteCompanyItemFromList);
        }
      },
      [actions, pushErrorToast, pushSuccessToast]
    )
  );
}
